import { useEffect, useState } from "react";
import Card from "components/card";
import StatusBadge from "components/admin/StatusBadge";
import { apiGet } from "lib/apiClient";
import ChangePasswordForm from "components/account/ChangePasswordForm";
import TwoFactorSettings from "components/account/TwoFactorSettings";
import { useAuth, organisationLabel } from "contexts/AuthContext";

const STATUS_TONE = { ACTIVE: "green", PENDING_VERIFICATION: "amber", LOCKED: "red", DISABLED: "gray" };

export default function ProfilePage() {
  const { portal, isPartner } = useAuth();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiGet("/auth/me")
      .then(setDetail)
      .catch((err) => setError(err.message || "Failed to load profile."));
  }, []);

  const organisation = organisationLabel(portal);
  const roleLabel = isPartner
    ? portal?.canManage
      ? "Partner administrator"
      : "Partner teammate (view only)"
    : portal?.canManage
    ? "Tenant administrator"
    : "Tenant team member (view only)";

  return (
    <div>
      {error && <div className="mb-4 rounded-xl bg-red-50 p-3 text-sm font-medium text-red-500">{error}</div>}
      {detail && (
        <Card extra="p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xl font-semibold text-navy-700 dark:text-white">{detail.user.email}</p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{detail.user.phone || "No phone on file"}</p>
            </div>
            <StatusBadge label={detail.user.status} tone={STATUS_TONE[detail.user.status] || "gray"} />
          </div>

          {portal && (
            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-gray-200 p-4 dark:border-white/10">
                <p className="text-xs font-medium text-muted-foreground">Organization</p>
                <p className="mt-1 text-sm font-semibold text-navy-700 dark:text-white">{organisation}</p>
              </div>
              <div className="rounded-xl border border-gray-200 p-4 dark:border-white/10">
                <p className="text-xs font-medium text-muted-foreground">Your role</p>
                <p className="mt-1 text-sm font-semibold text-navy-700 dark:text-white">{roleLabel}</p>
              </div>
            </div>
          )}

          {!isPartner && (
            <>
              <p className="mt-6 text-sm font-semibold text-navy-700 dark:text-white">Access</p>
              <div className="mt-2">
                {detail.grants.length === 0 ? (
                  <p className="text-sm text-gray-500">No portal access grants.</p>
                ) : (
                  detail.grants.map((g, i) => (
                    <div key={i} className="flex items-center justify-between border-b border-gray-100 py-2 text-sm last:border-none dark:border-white/10">
                      <span className="text-navy-700 dark:text-white">{g.permissionKey}</span>
                      <span className="text-xs text-gray-500">{g.tenantId || "platform-wide"}</span>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </Card>
      )}
      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-2">
        <Card extra="p-6">
          {detail ? <TwoFactorSettings defaultPhone={detail.user.phone || ""} /> : <div className="h-32 animate-pulse rounded-md bg-accent" />}
        </Card>
        <Card extra="p-6">
          <p className="text-lg font-semibold text-navy-700 dark:text-white">Change password</p>
          <p className="mb-4 mt-1 text-sm text-gray-600 dark:text-gray-300">Changing it signs out every other device. Your primary contact is told by email.</p>
          <ChangePasswordForm />
        </Card>
      </div>
    </div>
  );
}
